<template>
  <div class="instagram-condition-root">
    <div class="campaign-create-condition">
      <Panel>
        <div class="condition-stack">
          <div class="condition-header">
            <span class="condition-title">キャンペーンタイプ</span>
          </div>

          <div class="campaign-type-or-entry-action">
            <el-select :value="campaign_type" @change="changeCampaignType" :disabled="is_action_type_disabled">
              <el-option
                v-for="(option, index) in campaign_type_options"
                :key="index"
                :value="option.value"
                :label="option.label"
              />
            </el-select>
          </div>

          <div class="campaign-type-or-entry-action message-text" v-if="is_keyword_type">
            Instagram上で応募が完結する、後日抽選のキャンペーンです。
          </div>

          <div class="campaign-type-or-entry-action message-text" v-if="is_instantwin_type">
            キャンペーン応募後、すぐに当選結果がわかる仕組みです。
            <br />
            応募条件を満たしている場合のみ、抽選が行なわれます。
          </div>

          <div class="campaign-type-or-entry-action condition-header">
            <span class="condition-title">応募条件</span>
          </div>

          <div class="campaign-type-or-entry-action">
            <el-select :value="entry_action_type" @change="changeCampaignActionType">
              <el-option
                v-for="(option, index) in entry_action_type_options"
                :key="index"
                :value="option.value"
                :label="option.label"
                :disabled="is_condition_disabled"
              />
            </el-select>
          </div>

          <div v-if="is_mention_type" class="condition-description">
            特定のハッシュタグと実施アカウントへのメンションを含む投稿
          </div>
          <div v-else class="condition-description">特定のハッシュタグを含む実施アカウントの投稿に対するコメント</div>

          <div class="condition-image">
            <img v-if="is_mention_type" src="@/client/assets/images/instagram-condition-mention.png" />
            <img v-else src="@/client/assets/images/instagram-condition-comment.png" />
          </div>

          <div v-if="!is_mention_type && !is_instantwin_type" class="condition-tips">
            <Tips>
              フィード上で公開されない広告投稿(通称: ダークポストなど)へのコメントは、APIの制限により収集できません。
            </Tips>
          </div>
        </div>
      </Panel>
    </div>

    <div class="campaign-create-condition">
      <Panel>
        <div class="condition-stack">
          <div class="condition-header">
            <span class="condition-title">以下のキャンペーンハッシュタグ全てを含む</span>
            <span class="badge badge-danger">必須</span>
          </div>

          <div class="condition-keywords">
            <Tags
              :value="keywords"
              :editable="!is_condition_disabled"
              :max-length="120"
              @input="changeKeywords"
              sns-type="instagram"
            />
          </div>

          <div v-if="!is_instantwin_type" class="condition-description">
            <div class="description-danger">応募の振り分けについて</div>
            応募の振り分けをする場合、キャンペーン作成後に応募グループを追加する必要があります。
          </div>
        </div>
      </Panel>
    </div>

    <!-- 抽選ルール（Instagramインスタントウィンのみ） -->
    <div v-if="is_instantwin_type" class="campaign-create-condition">
      <Panel>
        <div class="condition-stack">
          <div class="condition-header">
            <span class="condition-title">抽選ルール</span>
          </div>

          <!-- 1日1回抽選 -->
          <div class="input-container">
            <div class="switch-with-help">
              <el-switch
                v-model="is_daily_lottery"
                :disabled="is_condition_disabled"
                active-text="1日1回抽選"
                :active-value="true"
                :inactive-value="false"
              ></el-switch>
              <HelpTooltip>
                ONにすると、1日の切り替え時間を経過後、応募者の過去の応募履歴に関わらず再度抽選します。
              </HelpTooltip>
            </div>
          </div>

          <!-- 別キャンペーンで当選実績がある場合、落選にする -->
          <div class="input-container">
            <div class="switch-with-help">
              <el-switch
                v-model="exclude_other_campaign_winner"
                :disabled="is_condition_disabled"
                active-text="別キャンペーンで当選実績がある場合、落選にする"
                :active-value="true"
                :inactive-value="false"
              ></el-switch>
              <HelpTooltip> グループ内の別キャンペーンで、1回以上当選している場合、落選となります。 </HelpTooltip>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { mapActions, mapState } from 'pinia'
import { isValidHashtag } from 'twitter-text'
import { defineComponent } from 'vue'

import HelpTooltip from '@/client/components/_atoms/HelpTooltip.vue'
import Message from '@/client/components/_atoms/Message.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import Tips from '@/client/components/_atoms/Tips.vue'
import Tags from '@/client/components/_molecules/Tags.vue'
import { CAMPAIGN_STATUS, INSTAGRAM_CAMPAIGN_TYPES, INSTAGRAM_ENTRY_ACTION_TYPES } from '@/client/constant'
import { TrackingService } from '@/client/services'
import { CampaignForm, useCampaignDialogStore } from '@/client/store/useCampaignDialogStore'
import { useSystemStore } from '@/client/store/useSystemStore'

export default defineComponent({
  name: 'CampaignCreateInstagramCondition',
  components: {
    Message,
    Panel,
    Tips,
    Tags,
    HelpTooltip
  },
  methods: {
    ...mapActions(useCampaignDialogStore, ['setForm']),
    cancel(payload: unknown) {
      this.$emit('cancel', payload)
      return payload
    },
    /**
     * キャンペーンタイプを変更
     */
    async changeCampaignActionType(entry_action_type: CampaignForm['entry_action_type']) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|応募条件(IG)|応募条件')

      if (entry_action_type === 'mention' && this.form.campaign_type === 'instantwin') {
        entry_action_type = 'comment'
      }

      const form = {
        ...this.form,
        entry_action_type
      }

      await this.setForm(form)
    },
    async changeCampaignType(campaign_type: CampaignForm['campaign_type']) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|応募条件(IG)|キャンペーンタイプ')

      const form = {
        ...this.form,
        campaign_type
      }

      if (campaign_type === 'instantwin' && this.form.entry_action_type === 'mention') {
        form.entry_action_type = 'comment'
      }

      await this.setForm(form)
    },
    /**
     * ハッシュタグを変更
     */
    async changeKeywords(value: string[]) {
      if (this.form.keywords?.length < value.length) {
        TrackingService.sendEvent('click:キャンペーンの新規作成|応募条件(IG)|全てを含む|追加')
      } else TrackingService.sendEvent('click:キャンペーンの新規作成|応募条件(IG)|全てを含む|削除')

      const is_valid = this.checkoutValidKeyword(value)

      if (!is_valid) {
        return
      }

      const form = {
        ...this.form,
        keywords: value
      }

      await this.setForm(form)
    },
    /**
     * ハッシュタグをチェック
     */
    checkoutValidKeyword(keywords: string[]) {
      const is_valid = keywords.every(keyword => isValidHashtag(keyword))

      if (!is_valid) {
        this.$notify({
          title: 'ハッシュタグに利用できない文字列が含まれています。',
          message: '',
          customClass: 'danger'
        })
      }

      return is_valid
    }
  },
  created() {
    if (!INSTAGRAM_ENTRY_ACTION_TYPES.map(v => v.value).includes(this.form.entry_action_type)) {
      this.changeCampaignActionType('comment')
    }
  },
  computed: {
    ...mapState(useCampaignDialogStore, ['form', 'api_campaign', 'mode']),
    ...mapState(useSystemStore, ['currentGroup']),
    entry_action_type() {
      return this.form.entry_action_type
    },
    campaign_type() {
      return this.form.campaign_type
    },
    keywords() {
      return this.form.keywords
    },
    campaign_type_options() {
      if (this.currentGroup.allowInInstantwin) {
        return INSTAGRAM_CAMPAIGN_TYPES
      }

      return INSTAGRAM_CAMPAIGN_TYPES.filter(v => v.value !== 'instantwin')
    },
    is_daily_lottery: {
      get() {
        return Boolean(this.form.is_daily_lottery)
      },
      set(value: boolean) {
        const form: CampaignForm = {
          ...this.form,
          is_daily_lottery: value
        }

        this.setForm(form)
      }
    },
    exclude_other_campaign_winner: {
      get() {
        return Boolean(this.form.exclude_other_campaign_winner)
      },
      set(value: boolean) {
        const form: CampaignForm = {
          ...this.form,
          exclude_other_campaign_winner: value
        }

        this.setForm(form)
      }
    },
    entry_action_type_options() {
      if (this.form.campaign_type === 'instantwin') {
        return INSTAGRAM_ENTRY_ACTION_TYPES.filter(v => v.value !== 'mention')
      }

      return INSTAGRAM_ENTRY_ACTION_TYPES
    },
    is_instantwin_type() {
      return this.form.campaign_type === 'instantwin'
    },
    is_keyword_type() {
      return this.form.campaign_type === 'keyword'
    },
    is_mention_type() {
      return this.form.entry_action_type === 'mention'
    },
    is_action_type_disabled() {
      return this.api_campaign && this.mode === 'edit'
    },
    is_condition_disabled() {
      return (
        this.api_campaign &&
        [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
          this.api_campaign.status
        ) &&
        this.mode === 'edit'
      )
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-condition-root {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit * 2;
}

.condition-stack {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
}

.condition-description {
  font-size: $font-small-size;
  color: $sub-text-color;
}

.condition-image {
  text-align: center;
  border: 1px solid $border-color;
  border-radius: 4px;

  img {
    max-width: $spacing-unit * 60;
  }
}

.condition-header {
  .condition-title {
    font-weight: bold;
  }

  .badge {
    margin-left: $spacing-unit;
  }
}

.message-text {
  font-size: $font-small-size;
  color: $sub-text-color;
}

.description-danger {
  color: $danger-color;
}

.condition-tips {
  margin-top: $spacing-unit;
}

.input-container {
  width: 100%;

  &:not(:last-child) {
    margin-bottom: $spacing-unit * 2;
  }
}

.switch-with-help {
  display: flex;
  align-items: center;
}
</style>
